import React, { useCallback, useEffect, useState } from 'react';
import {
  TaskyButton,
  TaskyEmptyState,
  TaskyErrorState,
  TaskySpinner,
  TaskyTable,
  TaskyTableBody,
  TaskyTableCell,
  TaskyTableHead,
  TaskyTableHeaderCell,
  TaskyTableRow,
} from '../../tasky';
import { AdminLoginHistoryRow, PaginatedResult } from '../../../services/api/adminUserDetailService';

const LOGIN_TYPE_LABELS: Record<string, string> = {
  email: 'メール',
  firebase: 'Firebase',
  admin: '管理者',
  onboarding_token: 'オンボーディング',
  trial: 'トライアル',
  lead: 'リード',
};

export interface LoginHistoryListProps {
  fetchPage: (page: number) => Promise<PaginatedResult<AdminLoginHistoryRow>>;
  // 組織詳細ページから使う場合はユーザー列を表示する
  showUserColumn?: boolean;
}

const LoginHistoryList = React.memo<LoginHistoryListProps>(({ fetchPage, showUserColumn = false }) => {
  const [rows, setRows] = useState<AdminLoginHistoryRow[]>([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (targetPage: number) => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchPage(targetPage);
      setRows(result.data);
      setPage(result.current_page);
      setLastPage(result.last_page);
      setTotal(result.total);
    } catch (err) {
      console.error('Failed to fetch login histories:', err);
      setError('ログイン履歴の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [fetchPage]);

  useEffect(() => {
    load(1);
  }, [load]);

  if (loading && rows.length === 0) {
    return (
      <div className="flex items-center justify-center gap-3 py-12 text-sm text-gray-500">
        <TaskySpinner size="md" />
        読み込み中...
      </div>
    );
  }

  if (error) {
    return <TaskyErrorState description={error} />;
  }

  if (rows.length === 0) {
    return <TaskyEmptyState title="ログイン履歴がありません" />;
  }

  return (
    <div>
      <TaskyTable>
        <TaskyTableHead>
          <TaskyTableRow hoverable={false}>
            <TaskyTableHeaderCell>日時</TaskyTableHeaderCell>
            <TaskyTableHeaderCell>種別</TaskyTableHeaderCell>
            {showUserColumn && <TaskyTableHeaderCell>ユーザー</TaskyTableHeaderCell>}
            <TaskyTableHeaderCell>IPアドレス</TaskyTableHeaderCell>
            <TaskyTableHeaderCell>User Agent</TaskyTableHeaderCell>
          </TaskyTableRow>
        </TaskyTableHead>
        <TaskyTableBody>
          {rows.map((row) => (
            <TaskyTableRow key={row.id}>
              <TaskyTableCell className="whitespace-nowrap text-xs text-gray-500">
                {new Date(row.created_at).toLocaleString('ja-JP')}
              </TaskyTableCell>
              <TaskyTableCell>{LOGIN_TYPE_LABELS[row.login_type] || row.login_type}</TaskyTableCell>
              {showUserColumn && (
                <TaskyTableCell>
                  {row.user ? (
                    <div>
                      <div className="text-sm text-dark">{row.user.name}</div>
                      <div className="text-xs text-gray-500">{row.user.email}</div>
                    </div>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </TaskyTableCell>
              )}
              <TaskyTableCell className="text-xs text-gray-500 whitespace-nowrap">{row.ip_address || '-'}</TaskyTableCell>
              <TaskyTableCell
                className="max-w-[280px] truncate text-xs text-gray-500"
                title={row.user_agent || undefined}
              >
                {row.user_agent || '-'}
              </TaskyTableCell>
            </TaskyTableRow>
          ))}
        </TaskyTableBody>
      </TaskyTable>

      {lastPage > 1 && (
        <div className="flex items-center justify-center gap-3 mt-4">
          <TaskyButton
            variant="outline"
            size="sm"
            onClick={() => load(page - 1)}
            disabled={page <= 1 || loading}
          >
            前へ
          </TaskyButton>
          <span className="text-sm text-gray-500">{page} / {lastPage}（全{total}件）</span>
          <TaskyButton
            variant="outline"
            size="sm"
            onClick={() => load(page + 1)}
            disabled={page >= lastPage || loading}
          >
            次へ
          </TaskyButton>
        </div>
      )}
    </div>
  );
});
LoginHistoryList.displayName = 'LoginHistoryList';

export default LoginHistoryList;
